/**
 * Cognito authentication service.
 *
 * Wraps `amazon-cognito-identity-js` to provide a clean async API for
 * sign-up, sign-in, sign-out, session restoration, and token refresh.
 */
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
  CognitoIdToken,
  CognitoAccessToken,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import { env } from '../utils/env';
import {
  ACCOUNT_LINK_REQUIRED_ERROR,
  parseAccountLinkEmail,
} from '../utils/ssoLinkEmail';

export { ACCOUNT_LINK_REQUIRED_ERROR };
const PKCE_VERIFIER_KEY = 'lumina.oauth.code_verifier';
const SSO_FLOW_KEY = 'lumina.sso.flow';

export type SsoFlow = 'google' | 'linked' | '';

const poolData = {
  UserPoolId: env.COGNITO_USER_POOL_ID,
  ClientId: env.COGNITO_CLIENT_ID,
};

let userPool: CognitoUserPool | null = null;

function getUserPool(): CognitoUserPool {
  if (!userPool) {
    if (!poolData.UserPoolId || !poolData.ClientId) {
      throw new Error(
        'Cognito is not configured. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID in .env.local (run ./setup-env.sh).'
      );
    }
    userPool = new CognitoUserPool(poolData);
  }
  return userPool;
}

export interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

export interface CognitoUserInfo {
  userId: string;
  email: string;
  name: string;
  organizationId?: string;
  groups: string[];
}

export interface AuthResult {
  tokens: CognitoTokens;
  user: CognitoUserInfo;
}

function parseCognitoGroups(raw: unknown): string[] {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((g) => String(g).trim()).filter(Boolean);
  }
  const text = String(raw).trim();
  if (!text) return [];
  if (text.startsWith('[') && text.endsWith(']')) {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.map((g) => String(g).trim()).filter(Boolean);
      }
    } catch {
      /* fall through */
    }
  }
  if (text.includes(',')) {
    return text.split(',').map((g) => g.trim()).filter(Boolean);
  }
  if (text.includes(' ')) {
    return text.split(/\s+/).map((g) => g.trim()).filter(Boolean);
  }
  return [text];
}

function extractUserInfo(session: CognitoUserSession): CognitoUserInfo {
  const idToken = session.getIdToken();
  const payload = idToken.decodePayload();
  return {
    userId: payload['sub'] as string,
    email: (payload['email'] as string) || '',
    name: (payload['name'] as string) || (payload['email'] as string)?.split('@')[0] || '',
    organizationId: (payload['custom:organization-id'] as string) || undefined,
    groups: parseCognitoGroups(payload['cognito:groups']),
  };
}

function extractTokens(session: CognitoUserSession): CognitoTokens {
  return {
    idToken: session.getIdToken().getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
  };
}

export function signUp(
  email: string,
  password: string,
  name: string
): Promise<{ userSub: string }> {
  return new Promise((resolve, reject) => {
    const attributes = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoUserAttribute({ Name: 'name', Value: name }),
    ];

    getUserPool().signUp(email, password, attributes, [], (err, result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve({ userSub: result?.userSub ?? '' });
    });
  });
}

export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });
    cognitoUser.confirmRegistration(code, true, (err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

export function signIn(email: string, password: string): Promise<AuthResult> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        resolve({
          tokens: extractTokens(session),
          user: extractUserInfo(session),
        });
      },
      onFailure: (err) => {
        reject(err);
      },
    });
  });
}

export function signOut(): void {
  const cognitoUser = getUserPool().getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
}

export function getCurrentSession(): Promise<AuthResult | null> {
  return new Promise((resolve) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }
        resolve({
          tokens: extractTokens(session),
          user: extractUserInfo(session),
        });
      }
    );
  });
}

/**
 * Refresh Cognito tokens for the current user.
 *
 * Prefer getSession() which already refreshes expired ID/access tokens.
 * Only force refreshSession when the returned session is still invalid
 * (avoids concurrent double-refresh races).
 */
export function refreshSession(): Promise<AuthResult | null> {
  return new Promise((resolve) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          resolve(null);
          return;
        }

        if (session.isValid()) {
          resolve({
            tokens: extractTokens(session),
            user: extractUserInfo(session),
          });
          return;
        }

        const refreshToken = session.getRefreshToken();
        cognitoUser.refreshSession(
          refreshToken,
          (refreshErr: Error | null, newSession: CognitoUserSession) => {
            if (refreshErr || !newSession || !newSession.isValid()) {
              resolve(null);
              return;
            }
            resolve({
              tokens: extractTokens(newSession),
              user: extractUserInfo(newSession),
            });
          }
        );
      }
    );
  });
}

export function resendConfirmationCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });
    cognitoUser.resendConfirmationCode((err) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });
    cognitoUser.forgotPassword({
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function confirmNewPassword(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool(),
    });
    cognitoUser.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

function oauthRedirectUri(): string {
  return `${window.location.origin}/auth/callback`;
}

function hostedUiBase(): string {
  const domain = env.COGNITO_DOMAIN.replace(/^https?:\/\//, '').replace(/\/$/, '');
  if (!domain) {
    throw new Error(
      'Cognito Hosted UI is not configured. Set VITE_COGNITO_DOMAIN in .env.local (run ./setup-env.sh).'
    );
  }
  return `https://${domain}`;
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

async function sha256Base64Url(plain: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(plain));
  const bytes = new Uint8Array(digest);
  let binary = '';
  bytes.forEach((b) => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function setSsoFlow(flow: SsoFlow): void {
  if (!flow) {
    sessionStorage.removeItem(SSO_FLOW_KEY);
    return;
  }
  sessionStorage.setItem(SSO_FLOW_KEY, flow);
}

export function getSsoFlow(): SsoFlow {
  return (sessionStorage.getItem(SSO_FLOW_KEY) as SsoFlow) || '';
}

export async function startGoogleSignIn(): Promise<void> {
  const verifier = randomVerifier();
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  setSsoFlow('google');
  const challenge = await sha256Base64Url(verifier);
  const params = new URLSearchParams({
    identity_provider: 'Google',
    redirect_uri: oauthRedirectUri(),
    response_type: 'code',
    client_id: env.COGNITO_CLIENT_ID,
    scope: 'openid email profile',
    code_challenge_method: 'S256',
    code_challenge: challenge,
  });
  window.location.assign(`${hostedUiBase()}/oauth2/authorize?${params.toString()}`);
}

export class AccountLinkRequiredError extends Error {
  email: string;

  constructor(email: string) {
    super(ACCOUNT_LINK_REQUIRED_ERROR);
    this.name = 'AccountLinkRequiredError';
    this.email = email;
  }
}

function persistFederatedSession(idToken: string, accessToken: string, refreshToken: string): AuthResult {
  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: idToken }),
    AccessToken: new CognitoAccessToken({ AccessToken: accessToken }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: refreshToken }),
  });
  const userInfo = extractUserInfo(session);
  const username = userInfo.email || userInfo.userId;
  const cognitoUser = new CognitoUser({
    Username: username,
    Pool: getUserPool(),
  });
  cognitoUser.setSignInUserSession(session);
  return { tokens: extractTokens(session), user: userInfo };
}

export async function completeGoogleRedirect(search: string): Promise<AuthResult> {
  const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  const error = params.get('error');
  const errorDescription = params.get('error_description') || '';
  if (error) {
    const linkEmail = parseAccountLinkEmail(errorDescription);
    if (linkEmail !== null) {
      throw new AccountLinkRequiredError(linkEmail);
    }
    throw new Error(
      error === 'access_denied'
        ? 'Google sign-in was cancelled.'
        : 'Google sign-in did not complete. Try again.'
    );
  }

  const code = params.get('code');
  if (!code) {
    throw new Error('Google sign-in did not complete. Try again.');
  }

  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY) || '';
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: env.COGNITO_CLIENT_ID,
    code,
    redirect_uri: oauthRedirectUri(),
  });
  if (verifier) {
    body.set('code_verifier', verifier);
  }

  const response = await fetch(`${hostedUiBase()}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });
  if (!response.ok) {
    throw new Error('Could not complete Google sign-in. Try again.');
  }
  const tokens = (await response.json()) as {
    id_token?: string;
    access_token?: string;
    refresh_token?: string;
  };
  if (!tokens.id_token || !tokens.access_token) {
    throw new Error('Could not complete Google sign-in. Try again.');
  }
  return persistFederatedSession(
    tokens.id_token,
    tokens.access_token,
    tokens.refresh_token || ''
  );
}

export function changePassword(
  oldPassword: string,
  newPassword: string
): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      reject(new Error('No authenticated user'));
      return;
    }

    cognitoUser.getSession((err: Error | null) => {
      if (err) {
        reject(err);
        return;
      }
      cognitoUser.changePassword(oldPassword, newPassword, (changeErr) => {
        if (changeErr) {
          reject(changeErr);
          return;
        }
        resolve();
      });
    });
  });
}
